import React from 'react'
import {Heart, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { motion } from 'framer-motion'

interface Testimonial {
  _id: string
  name: string
  message: string
  createdAt: string
}

interface TestimonialsWallProps {
  testimonials: Testimonial[]
  loading: boolean
  limit?: number
  showAll?: boolean
}

const TestimonialsWall: React.FC<TestimonialsWallProps> = ({ testimonials, loading, limit, showAll = false }) => {
  const displayTestimonials = limit ? testimonials.slice(0, limit) : testimonials

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6b8f71]"></div>
        <p className="ml-4 text-gray-600">Carregando depoimentos...</p>
      </div>
    )
  }

  if (displayTestimonials.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-md">
        <MessageCircle className="mx-auto text-gray-300 mb-4" size={48} />
        <p>Nenhum depoimento aprovado ainda.</p>
      </div>
    )
  }

  return (
    <div className={showAll ? 'space-y-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
      {displayTestimonials.map((testimonial, index) => (
        <motion.div
          key={testimonial._id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className={`bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow ${
            showAll ? 'flex items-start gap-4' : ''
          }`}
        >
          <div className={`${showAll ? 'bg-[#6b8f71]/10 p-3 rounded-full flex-shrink-0' : 'flex items-start gap-3 mb-4'}`}>
            <Heart className={`text-[#6b8f71] ${showAll ? '' : 'flex-shrink-0 mt-1'}`} size={24} />
            {!showAll && (
              <div className="flex-1">
                <p className="text-gray-700 leading-relaxed line-clamp-4">{testimonial.message}</p>
              </div>
            )}
          </div>
          
          {showAll && (
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-[#1e3a8a]">{testimonial.name}</h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                  Aprovado
                </span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap mb-3">{testimonial.message}</p>
              <p className="text-xs text-gray-500">
                {new Date(testimonial.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          )}

          {!showAll && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {new Date(testimonial.createdAt).toLocaleDateString('pt-BR')}
              </span>
              <span className="text-[#6b8f71] font-semibold flex items-center gap-1">
                <Heart size={14} />
                {testimonial.name}
              </span>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  )
}

export default TestimonialsWall
